import { Cookie, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';

/**
 * Banner de consentimento de cookies/tracking (LGPD art. 8 - consentimento).
 *
 * Mostra na primeira visita do usuário não-autenticado (clientes logados
 * já consentiram via onboarding/termos). Opcoes:
 * - Aceitar tudo
 * - Apenas essenciais (analytics off)
 * - Personalizar (link para /lgpd)
 *
 * Persiste em localStorage `octawash:cookie-consent`:
 *   { essenciais: true, analytics: bool, dta: ISO }
 *
 * Quando usuário aceita, o gate de PostHog/analytics deve consultar
 * essa preferência antes de inicializar (deferred).
 *
 * Sprint AAAA.
 */

const LS_KEY = 'octawash:cookie-consent';

interface Consent {
  essenciais: boolean;
  analytics: boolean;
  dta: string;
}

function lerConsent(): Consent | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as Consent;
  } catch {
    return null;
  }
}

function salvarConsent(analytics: boolean) {
  try {
    const c: Consent = {
      essenciais: true,
      analytics,
      dta: new Date().toISOString(),
    };
    localStorage.setItem(LS_KEY, JSON.stringify(c));
  } catch {
    // ignore (ex: private browsing)
  }
}

export function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Mostra so se ainda não consentiu
    if (!lerConsent()) {
      setVisible(true);
    }
  }, []);

  function aceitar(analytics: boolean) {
    salvarConsent(analytics);
    setVisible(false);
    // Notifica outros componentes (analytics gate) via evento custom
    window.dispatchEvent(new CustomEvent('octawash:consent-changed'));
  }

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 border-t bg-white p-4 shadow-2xl md:bottom-4 md:left-1/2 md:right-auto md:max-w-2xl md:-translate-x-1/2 md:rounded-lg md:border"
      role="region"
      aria-label="Consentimento de cookies"
    >
      <div className="flex items-start gap-3">
        <div className="bg-primary/10 mt-0.5 hidden rounded-md p-2 sm:block">
          <Cookie className="text-primary h-5 w-5" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold">Sua privacidade importa</p>
          <p className="text-muted-foreground mt-1 text-xs">
            Usamos cookies essenciais para o funcionamento do servico. Cookies de analise nos ajudam
            a melhorar o app — voce decide. Veja os detalhes em{' '}
            <Link to="/lgpd" className="text-primary underline hover:no-underline">
              meus dados
            </Link>{' '}
            e na{' '}
            <a
              href="/politica-privacidade"
              className="text-primary underline hover:no-underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              politica de privacidade
            </a>
            .
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" onClick={() => aceitar(true)}>
              Aceitar todos
            </Button>
            <Button size="sm" variant="outline" onClick={() => aceitar(false)}>
              Apenas essenciais
            </Button>
            <Button size="sm" variant="ghost" asChild>
              <Link to="/lgpd">Personalizar</Link>
            </Button>
          </div>
        </div>
        <button
          type="button"
          onClick={() => aceitar(false)}
          aria-label="Fechar (apenas essenciais)"
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

/**
 * Helper para outros componentes lerem o consent atual.
 * Retorna `null` se ainda não consentiu, ou objeto Consent.
 */
export function useCookieConsent(): Consent | null {
  const [c, setC] = useState<Consent | null>(() => lerConsent());

  useEffect(() => {
    function handler() {
      setC(lerConsent());
    }
    window.addEventListener('octawash:consent-changed', handler);
    return () => window.removeEventListener('octawash:consent-changed', handler);
  }, []);

  return c;
}
